import {
  useEffect, useMemo, useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import useGetMetric from 'hooks/useGetMetric';
import { Mixpanel } from 'mixpanel';
import { IData } from 'features/ProductConverter';
import Select, { Option } from 'components/Select';
import MaskInput from 'components/MaskedInput';
import Button from 'components/Button';
import useUser from 'api/hooks/useUser';
import { numberFormatter, unMaskInput } from 'utils/helpers';
import { AlkaliProps, SelectProps } from 'utils/types';

interface Props {
  data: AlkaliProps[];
  omyaData: AlkaliProps[];
  setComparisonData: (data: IData | null) => void;
  setResult: (value: boolean) => void;
}

const ProductConverterInitial = ({
  data,
  omyaData,
  setComparisonData,
  setResult,
}: Props) => {
  const { t } = useTranslation();
  const { control, watch } = useForm<IData>();
  const watchConsumption = watch(['consumption']);
  const watchPrice = watch(['price']);
  const watchCostDelivered = watch(['costDelivered']);
  const [alkali, setAlkali] = useState<SelectProps | null>(null);
  const [concentration, setConcentration] = useState<SelectProps | null>(null);
  const [omya, setOmya] = useState<SelectProps | null>(null);
  const [concentrationOmya, setConcentrationOmya] =
    useState<SelectProps | null>(null);
  const { ton } = useGetMetric();
  const { currencyBlue } = useCurrency();
  const { data: user } = useUser();

  const annualExpenditureValue = useMemo(() => {
    const watchPrice1 = unMaskInput(watchPrice?.[0]);
    const watchConsumption1 = unMaskInput(watchConsumption?.[0]);

    if (watchPrice?.[0]?.length > 0 && watchConsumption?.[0]?.length) {
      return numberFormatter(Number(watchPrice1) * Number(watchConsumption1));
    } else {
      return 0;
    }
  }, [watchPrice, watchConsumption]);

  const onSubmit = () => {
    const result = {
      consumption: watchConsumption?.[0],
      price: watchPrice?.[0],
      costDelivered: watchCostDelivered[0],
      alkali: alkali?.value ? alkali?.value : '',
      concentration: concentration?.value ? concentration?.value : '',
      omya: omya?.value ? omya?.value : '',
      concentrationOmya: concentrationOmya?.value
        ? concentrationOmya?.value
        : '',
      annualExpenditure: annualExpenditureValue,
    };
    setComparisonData(result);
    setResult(true);

    Mixpanel.track('Product Converter Comparison', {
      role: user?.role?.name,
      organization: user?.organization,
    });
  };

  const alkaliSelectOptions = useMemo(() => data?.map((alkali) => ({
    value: alkali?.['Material A'],
    label: alkali?.['Material A'],
  })), [data]);

  const omyaSelectOptions = useMemo(() => omyaData?.map((omya) => ({
    value: omya?.['Material A'],
    label: omya?.['Material A'],
  })), [omyaData]);

  function getUniqueListBy(arr: SelectProps[], key: string) {
    return [...new Map(arr?.map((item: any) => [item[key], item])).values()];
  }

  const arr1 = getUniqueListBy(alkaliSelectOptions, 'value');
  const arr2 = getUniqueListBy(omyaSelectOptions, 'value');

  const getConcentrationList = useMemo(() => {
    const result: AlkaliProps[] = [];

    if (alkali?.value) {
      data?.forEach((alkaliItem: AlkaliProps) => {
        if (alkaliItem?.['Material A'] === alkali?.value) {
          result.push(alkaliItem);
        }
      });
    }

    return result.map((concentration: AlkaliProps) => ({
      value: String(concentration.Concentration),
      label:
          String(concentration.Concentration) === '100'
            ? String(concentration.Concentration) + ' = Powder'
            : String(concentration.Concentration) + ' %',
    }));
  }, [alkali]);

  const getConcentrationListOmya = useMemo(() => {
    const result: AlkaliProps[] = [];

    if (omya?.value) {
      omyaData?.forEach((omyaItem: AlkaliProps) => {
        if (omyaItem?.['Material A'] === omya?.value) {
          result.push(omyaItem);
        }
      });
    }

    return result.map((concentration: AlkaliProps) => ({
      value: String(concentration.Concentration),
      label:
          String(concentration.Concentration) === '100'
            ? String(concentration.Concentration) + ' = Powder'
            : String(concentration.Concentration) + ' %',
    }));
  }, [omya]);

  useEffect(() => {
    if (getConcentrationList[0]) {
      setConcentration(getConcentrationList[0]);
    }
  }, [alkali]);

  useEffect(() => {
    if (getConcentrationListOmya[0]) {
      setConcentrationOmya(getConcentrationListOmya[0]);
    }
  }, [omya]);

  useEffect(() => {
    if (user) {
      Mixpanel.track('Product Converter Opened', {
        role: user?.role?.name,
        organization: user?.organization,
      });
    }
  }, [user]);

  const disabledState = useMemo(() => {
    if (
      alkali?.value &&
      omya?.value &&
      concentration?.value &&
      concentrationOmya?.value &&
      watchPrice?.[0]?.length > 0 &&
      watchCostDelivered?.[0]?.length > 0 &&
      watchConsumption?.[0]?.length
    ) {
      return false;
    }

    return true;
  }, [alkali, watchConsumption, watchPrice, concentration]);

  return (
    <form>
      <div className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl">
        <div className="flex justify-between md:flex-col">
          <div className="w-1/2 border-r border-primitiveGrey md:border-b md:border-r-0 pr-8 md:w-full md:pr-0">
            <div className="text-black font-bold text-xl md:text-lg">
              {t('currentProductTitle')}
            </div>
            <div className="my-6">
              <Select
                options={arr1}
                placeholder={t('selectValue')}
                title={t('currentAlkali')}
                value={[alkali as Option]}
                onChange={(value) => setAlkali(value)}
              />
            </div>
            <div className="my-6">
              <Select
                value={
                  concentration?.value
                    ? [
                      {
                        value: concentration.value,
                        label:
                            concentration.value === '100'
                              ? concentration.value + ' = Powder'
                              : concentration.value + ' %',
                      },
                    ]
                    : []
                }
                options={getConcentrationList}
                placeholder={t('selectValue')}
                title={`${t('concentration')} (wt.%)`}
                onChange={(value) => setConcentration(value)}
              />
            </div>
            <div className="my-6 w-full">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange } }) => (
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px]"
                    className="w-full"
                    title={`${t('annualConsumption')} (${ton})`}
                    placeholder={t('enterAnnualConsumption')}
                    onChange={onChange}
                  />
                )}
                name="consumption"
              />
            </div>
            <div className="my-6 w-full">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange } }) => (
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px]"
                    className="w-full"
                    title={`${t('price')} (${currencyBlue}/${ton})`}
                    placeholder={t('enterPrice')}
                    onChange={onChange}
                  />
                )}
                name="price"
              />
            </div>
            <div className="my-6 w-full relative">
              <div className="absolute top-[-10px] left-2 text-sm text-black">
                {t('currentExpenditure')}
              </div>
              <div className="bg-[#e2e2e2] p-3 rounded-xl text-secondaryText cursor-not-allowed">
                {annualExpenditureValue ? annualExpenditureValue : '0'}{' '}
                {currencyBlue}/{t('year')}
              </div>
            </div>
          </div>
          <div className="w-1/2 pl-8 md:w-full md:pl-0">
            <div className="text-black font-bold text-xl md:text-lg md:mt-6">
              {t('omyaOffered')}
            </div>
            <div className="my-6">
              <Select
                options={arr2}
                placeholder={t('selectValue')}
                title={t('product')}
                value={[omya as Option]}
                onChange={(value) => setOmya(value)}
              />
            </div>
            <div className="my-6">
              <Select
                value={
                  concentrationOmya?.value
                    ? [
                      {
                        value: concentrationOmya.value,
                        label:
                            concentrationOmya.value === '100'
                              ? concentrationOmya.value + ' = Powder'
                              : concentrationOmya.value + ' %',
                      },
                    ]
                    : []
                }
                options={getConcentrationListOmya}
                placeholder={t('selectValue')}
                title={`${t('concentration')} (wt.%)`}
                onChange={(value) => setConcentrationOmya(value)}
              />
            </div>
            <div className="my-6 w-full">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange } }) => (
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px]"
                    className="w-full"
                    title={`${t('costDelivered')} (${currencyBlue}/${ton})`}
                    placeholder={t('enterAnnualConsumption')}
                    onChange={onChange}
                  />
                )}
                name="costDelivered"
              />
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-center md:mt-6">
          <Button disabled={disabledState} onClick={onSubmit}>
            {t('comparison')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ProductConverterInitial;
